import { Controller } from '@hotwired/stimulus';

/**
 * Clipboard controller for copying text to clipboard
 *
 * Actions:
 * - copyProfileLink: Copies a profile link to the clipboard
 * - copyText: Copies arbitrary text to clipboard
 *
 * Data attributes:
 * - data-clipboard-url: The URL to copy
 * - data-clipboard-text: The text to copy
 * - data-clipboard-success-message-value: Custom success message (optional)
 *
 * Usage:
 * <button data-controller="clipboard" data-action="click->clipboard#copyProfileLink" data-clipboard-url="https://example.com">
 *   Copy link
 * </button>
 */
export default class extends Controller {
  static values = {
    successDuration: { type: Number, default: 2000 }, // Duration in ms to show success message
    successMessage: { type: String, default: 'Link has been copied' }, // Default success message
  };

  connect() {
    // Create notification element if it doesn't exist
    if (!document.getElementById('clipboard-notification')) {
      const notification = document.createElement('div');
      notification.id = 'clipboard-notification';
      notification.classList.add(
        'fixed',
        'top-4',
        'left-1/2',
        'transform',
        '-translate-x-1/2',
        'z-50',
        'bg-success',
        'text-white',
        'py-3',
        'px-4',
        'rounded-xl',
        'shadow-md',
        'flex',
        'items-center',
        'gap-3',
        'opacity-0',
        'min-w-[230px]',
        'transition-opacity',
        'justify-center',
        'duration-300'
      );
      notification.innerHTML = `
        <div>${this.checkIconSvg()}</div>
        <span id="clipboard-notification-text" class="text-sm font-medium">${
          this.successMessageValue
        }</span>
      `;
      document.body.appendChild(notification);
    }
  }

  copyProfileLink(event) {
    const url = event.currentTarget.dataset.clipboardUrl;
    if (url) {
      this.copyToClipboard(url);
    }
  }

  copyText(event) {
    const text = event.currentTarget.dataset.clipboardText;
    if (text) {
      this.copyToClipboard(text);
    }
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      this.showSuccessNotification();
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback method
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.showSuccessNotification();
        }
      } catch (err) {
        console.error('Fallback: Failed to copy text: ', err);
      }

      document.body.removeChild(textArea);
    }
  }

  showSuccessNotification() {
    const notification = document.getElementById('clipboard-notification');
    if (notification) {
      // Update the message text
      const textElement = document.getElementById(
        'clipboard-notification-text'
      );
      if (textElement) {
        textElement.textContent = this.successMessageValue;
      }

      // Show notification
      notification.classList.replace('opacity-0', 'opacity-100');

      // Hide notification after duration
      setTimeout(() => {
        notification.classList.replace('opacity-100', 'opacity-0');
      }, this.successDurationValue);
    }
  }

  // SVG icon for success notification
  checkIconSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>`;
  }
}
